import { useEffect, useRef } from 'react';
import {
  Client2ApiJobEndRequest,
  Client2ApiJobHeartbeatRequest,
  Client2ApiJobHeartbeatResponse,
  Client2ApiJobStartRequest,
  Client2ApiJobStartResponse,
} from 'common/deployment/JobDocuments';
import { useSocket } from 'client/lib/Socket';
import { ActiveLog, LogDeploymentInfo, LogSettings, useLogState } from 'client/state/LogState';

// Parameters for starting a log, this comes from log url
// /...[Deployment].../logs/[type]/[level1]/[level2][level3]
export type LogUrlParameters = {
  type: string;
  level1?: string;
  level2?: string;
  level3?: string;
  deployment?: LogDeploymentInfo;
};

export const useLog = () => {
  const { activeLog, dialog, initActiveLog, updateActiveLog, showLogDialog, hideLogDialog, toggleFullscreen, appendLogData } =
    useLogState();
  const socket = useSocket({});
  const heartbeatIntervalID = useRef<ReturnType<typeof setInterval> | null>(null);

  const closeActiveLog = async (activeLog: ActiveLog) => {
    const url = activeLog.settings.url;
    const jobUUID = activeLog.jobUUID;
    const deploymentID = activeLog.settings?.deployment?.deploymentID || null;

    if (jobUUID && deploymentID) {
      console.log('useLog: ⚡ Closing log', deploymentID, jobUUID);
      if (heartbeatIntervalID.current) clearTimeout(heartbeatIntervalID.current);
      const response = await socket.send<Client2ApiJobEndRequest, any>('/deployment/job/end', { deploymentID, jobUUID });
      if (response.status !== 'ok') {
        console.error('useLog: Failed to close log', url, deploymentID, jobUUID, response);
      }
    }
  };

  const logHeartbeat = async (deploymentID: number, jobUUID: string) => {
    const { status } = await socket.send<Client2ApiJobHeartbeatRequest, Client2ApiJobHeartbeatResponse>('/deployment/job/heartbeat', {
      jobUUID,
      deploymentID,
    });
    if (status !== 'ok') {
      console.log(`useLog: ⚡ Log heartbeat error ${deploymentID} ${jobUUID}`, status);
    } else {
      console.log(`useLog: ⚡ Log heartbeat ${deploymentID} ${jobUUID}`);
    }
  };

  const startLogFromUrlParameters = async (params: LogUrlParameters) => {
    const { type, level1, level2, level3, deployment } = params;

    // Basic sanity checks
    if (!['admin', 'container', 'command'].includes(type)) return { status: 'illegal type' as const };
    if (!level1) return { status: 'first parameter required' as const };

    // Create log url
    let url = '';
    if (deployment) {
      const { accountIdentifier, projectID, deploymentID } = deployment;
      url = [
        accountIdentifier,
        'project',
        projectID.toString(),
        'deployment',
        deploymentID.toString(),
        'logs',
        type,
        level1,
        ...(level2 ? [level2] : ''),
        ...(level2 && level3 ? [level3] : ''),
      ]
        .map(encodeURIComponent)
        .join('/');
    } else {
      return { status: 'non deployment logs not implmented' as const };
    }

    // Find component (if applicable)
    const component = deployment?.components.find((c) => c.namespace == params.level1);

    let settings: LogSettings;
    if (type == 'container') {
      if (!deployment) return { status: 'deployment required' as const };
      if (!component) return { status: 'component not found' as const };
      const { componentName, componentUrl, namespace } = component;

      settings = {
        type,
        url,
        title: 'Container Log',
        deployment,
        componentName,
        componentUrl,
        namespace,
        pod: level2 || null,
        container: (level2 && level3) || null,
      };
    } else if (type == 'admin') {
      settings = { type, url, title: 'Admin Log', deployment, adminLogIdentifier: level1 };
    } else {
      return { status: 'type not implemented' as const };
    }

    console.log(`useLog: startLog ${settings.url}`);

    // Close possibly running current job
    if (activeLog) await closeActiveLog(activeLog);

    initActiveLog(settings);

    const jobStartPayload: Client2ApiJobStartRequest = (() => {
      switch (settings.type) {
        case 'admin': {
          return { deploymentID: deployment.deploymentID, type: 'admin', adminLogIdentifier: settings.adminLogIdentifier };
        }
        /*case 'command': {
            const { namespace } = settings;
            const labelSelector = 'asd';
            return { deploymentID, type: 'command', namespace: 'ns', labelSelector, command: ['echo', '123'] };
          }*/
        case 'container': {
          const { namespace, pod, container } = settings;
          const labelSelector = 'asd';
          return { deploymentID: deployment.deploymentID, type: 'container', namespace, pod, container };
        }
        default:
          throw new Error('unknown log type');
      }
    })();

    console.log(`useLog: ⚡ Streaming log ${settings}`);
    const response = await socket.send<Client2ApiJobStartRequest, Client2ApiJobStartResponse>('/deployment/job/start', jobStartPayload);
    if (response.status !== 'ok') {
      updateActiveLog({ state: 'error', jobUUID: null, errorMessage: 'Something failed' });
      return { status: 'error starting job' as const };
    } else {
      updateActiveLog({ state: 'running', jobUUID: response.jobUUID });
      heartbeatIntervalID.current = setInterval(() => logHeartbeat(deployment.deploymentID, response.jobUUID), 300000); // TODO: Should be 00 more
    }

    return { status: 'ok' as const };
  };

  const openLogDialog = (params: LogUrlParameters) => {
    startLogFromUrlParameters(params);
    showLogDialog();
  };

  const closeLogDialog = () => {
    hideLogDialog();
    if (heartbeatIntervalID.current) {
      console.log('useLog: Closing heartbeat');
      clearTimeout(heartbeatIntervalID.current);
    }
    if (activeLog) {
      closeActiveLog(activeLog);
      console.log('useLog: Closing active log');
    }
  };

  return {
    activeLog,
    startLogFromUrlParameters,
    openLogDialog,
    closeLogDialog,
    isDialogOpen: dialog.open,
    isDialogFullscreen: dialog.fullscreen,
    toggleFullscreen,
    appendLogData,
  };
};
